/* eslint-disable no-nested-ternary */
import React, { useContext, useState } from 'react';
import { Tooltip, Link, Typography, Button, Paper } from '@material-ui/core';
import { useTheme, withStyles, makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Divider from '@material-ui/core/Divider';
import { Printer } from '@mummssoftware/icons';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import {
  TaskGlobalContext,
  UPDATE_WEEKLY_TASKS,
} from '@mummssoftware/global-context/TaskGlobalContext';
import {
  GlobalContext,
  REFRESH_GLOBAL_CONTEXT,
  RESET_ADMIN_DATE_RESTRICTIONS,
  refreshGlobalPatientHeader,
} from '@mummssoftware/global-context/GlobalContext';
import {
  PNGlobalContext,
  REFRESH_PN_GLOBAL_CONTEXT,
} from '@mummssoftware/global-context/PNGlobalContext';
import {
  VisitsGlobalContext,
  SET_PATIENT_VISITS,
} from '@mummssoftware/global-context/VisitsGlobalContext';
import { PDFContext } from '@mummssoftware/mumms-print';
import { PlanOfCare } from '../PlanOfCare';
import { WidgetMenu, WidgetPlusMenu } from '../WidgetMenu';
import { VisitMenu } from '../VisitMenu';
import { OrderDialog } from '../Orders/OrderDialog';
import { StandingOrdersMenu } from '../StandingOrders';
import { MaiaChatAgentRibbon } from '../ChartAI';
import {
  SideBarDiv,
  SidebarIcon,
  LowerSidebarIcon,
  StyledActionIcon,
  styleIcon,
  ModalDiv,
} from '../../components';
import { FacesheetMenu } from '../FaceSheetMenu';
import { DashboardContext } from '../Context/contexts/DashboardContext';
import { VisitContext } from '../Context/contexts/VisitContext';
import { Context } from '../../context';
import { FormListMenu } from '../FormListMenu';
import {
  MINIMIZE_VISIT_DIALOG,
  SET_PRINT_INTERFACE_OPEN,
  REFRESH_DASHBOARD,
  patientDashboard,
} from '../Context/constants';
import { ThemeMenu } from '../ThemeMenu';
import { MinimizedVisit } from '../VisitInterface/MinimizedVisit';
import defaults from '../../translations/en.json';

/** Same name for every `window.open` / link target so repeat clicks reuse one tab instead of `_blank`. */
const SMARTCHART_WINDOW_NAME = 'smartchart-embedded';

type SideBarProps = {
  config: mumms.WidgetConfig[];
  resolveUserNonRoleWidgets?: mumms.WidgetConfig[];
  onAddPatientDashboardWidget?: (selectedItem: mumms.WidgetConfig) => void;
  topBarHeight: number;
};

const HtmlTooltip = withStyles((theme: any) => ({
  tooltip: {
    backgroundColor: theme.palette.background.tooltip,
    color: theme.palette.common.black,
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  previewLink: {
    marginTop: 11,
    marginLeft: 2,
    marginRight: 0,
    width: 20,
    height: 20,
  },
  readOnlyLabel: {
    backgroundColor: `${theme.palette.error.main}`,
    flex: 1,
    color: 'white',
    fontWeight: 'bold',
    fontSize: '15px',
    letterSpacing: '1px',
    textAlign: 'center',
    userSelect: 'none',
    marginTop: 'auto',
    marginBottom: '8px',
    width: '25px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    '& > span': {
      transform: 'rotate(-90deg)',
      transformOrigin: 'center',
    },
  },
}));

// TODO fix ActionIcon in common ui
export const SideBar = ({
  config,
  resolveUserNonRoleWidgets,
  onAddPatientDashboardWidget,
  topBarHeight,
}: SideBarProps) => {
  const { palette } = useTheme() as any;

  const { dispatch } = useContext(DashboardContext);
  const { dispatchVisit, visitOpen, minimizeVisitDialog } = useContext(
    VisitContext,
  );
  const {
    patient,
    currentUser,
    dashboardType,
    agency,
    siteId,
    observation,
    pim,
    keycloak,
    clearscripts,
    idgMeeting,
  } = useContext(Context);
  const { resetAdminDateRestrictions } = useContext(GlobalContext);
  const classes = useStyles();

  const globalContext = useContext(GlobalContext);
  const pnGlobalContext = useContext(PNGlobalContext);
  const taskGlobalContext = useContext(TaskGlobalContext);
  const visitsGlobalContext = useContext(VisitsGlobalContext);

  const [
    smartChartEmbeddedOverlayOpen,
    setSmartChartEmbeddedOverlayOpen,
  ] = useState(false);

  const readOnlySidebar =
    window.env.readOnlySidebar || currentUser?.readOnly || patient?.readOnly;
  const isEmbeddedDashboard =
    new URLSearchParams(window.location.search).get('embedded') === 'true';
  const openPrintInterface = () =>
    dispatch({
      type: SET_PRINT_INTERFACE_OPEN,
      payload: true,
    });

  const clearDatesRestrictions = () =>
    globalContext.dispatch({
      type: RESET_ADMIN_DATE_RESTRICTIONS,
      payload: true,
    });
  const applyDatesRestrictions = () =>
    globalContext.dispatch({
      type: RESET_ADMIN_DATE_RESTRICTIONS,
      payload: false,
    });

  /**
   * 1. Widgets have data saved in Global Context => reset global data
   * 2. Reset keys
   */
  const refreshDashboard = () => {
    // REFRESH_GLOBAL_CONTEXT as of now refreshes care plan frequency endpoint calls
    globalContext.dispatch({
      type: REFRESH_GLOBAL_CONTEXT,
    });
    // REFRESH_PN_GLOBAL_CONTEXT as of now refreshes care plan problems endpoint calls: PN and approvals
    pnGlobalContext.dispatchPN({
      type: REFRESH_PN_GLOBAL_CONTEXT,
    });
    // REFRESH PATIENT ASSISTANCE
    taskGlobalContext.dispatchTask({
      type: UPDATE_WEEKLY_TASKS,
    });

    // refresh Patient Visits Log Widget
    visitsGlobalContext.dispatchVG({
      type: SET_PATIENT_VISITS,
      payload: null,
    });
    refreshPatientHeader();
    dispatch({
      type: REFRESH_DASHBOARD,
    });
  };
  /**
   * refreshPatientHeader
   */
  const refreshPatientHeader = async () => {
    const { patientNumber } = patient || {};
    if (!patientNumber) {
      return;
    }
    await refreshGlobalPatientHeader(
      pim,
      globalContext.dispatch,
      patientNumber,
      agency,
    );
  };

  const getDateRestrictionIcon = () => {
    // Mumms Admins and Hospice Admins
    if (currentUser.isMumms || currentUser.isHospiceAdmin) {
      const text = resetAdminDateRestrictions
        ? defaults.dateRestrictionOff
        : defaults.dateRestrictionOn;
      return (
        <HtmlTooltip
          title={
            <React.Fragment>
              <div>{text}</div>
            </React.Fragment>
          }
        >
          <IconButton
            onClick={
              resetAdminDateRestrictions
                ? applyDatesRestrictions
                : clearDatesRestrictions
            }
            style={{
              ...styleIcon,
              marginLeft: 0,
            }}
            color="primary"
            id="date-restrictions-ribbon"
          >
            {resetAdminDateRestrictions ? (
              <EventAvailableIcon htmlColor={palette.success.main} />
            ) : (
              <EventBusyIcon />
            )}
          </IconButton>
        </HtmlTooltip>
      );
    }
    return null;
  };

  const getSmartChart = () => {
    const { smartchartUrl } = window.env.hosts as any;

    const showSmartchartUrl =
      isEmbeddedDashboard &&
      patient &&
      smartchartUrl &&
      !readOnlySidebar &&
      keycloak.token;

    const handleSmartChartClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      if (!keycloak.token) {
        keycloak.logout();
        return;
      }
      try {
        await keycloak.updateToken(30);
        if (!keycloak.token) {
          keycloak.logout();
          return;
        }
        const url = `${smartchartUrl}/${patient?.patientNumber}?agency=${agency}&siteId=${siteId}&patientheader=true`;
        const win = window.open(url, SMARTCHART_WINDOW_NAME);
        if (win != null) {
          win.focus();
          if (isEmbeddedDashboard) {
            if (visitOpen && !minimizeVisitDialog) {
              dispatchVisit({
                type: MINIMIZE_VISIT_DIALOG,
                payload: true,
              });
            }
            setSmartChartEmbeddedOverlayOpen(true);
          }
        }
      } catch {
        keycloak.logout();
      }
    };

    return showSmartchartUrl ? (
      <HtmlTooltip
        title={
          <React.Fragment>
            <div>Smartchart</div>
          </React.Fragment>
        }
      >
        <Link
          external-link="true"
          variant="subtitle2"
          target={SMARTCHART_WINDOW_NAME}
          href={`${smartchartUrl}/${patient?.patientNumber}?agency=${agency}&siteId=${siteId}&patientheader=true`}
          onClick={handleSmartChartClick}
          id="preview-ribbon"
        >
          <OpenInNewIcon className={classes.previewLink} external-link="true" />
        </Link>
      </HtmlTooltip>
    ) : null;
  };
  const getPreviewEnv = () => {
    const windowEnvHosts = (window as any).env?.hosts;
    const showPreviewForBin = !(
      (window as any).env?.disableFeaturePreview.includes('*') ||
      (window as any).env?.disableFeaturePreview.includes(
        currentUser.currentHospiceBin,
      )
    );
    const previewHost =
      windowEnvHosts?.previewHost || process.env.REACT_APP_PREVIEW_HOST;
    const showPrevEnv = showPreviewForBin && !readOnlySidebar;

    return showPrevEnv ? (
      <HtmlTooltip
        title={
          <React.Fragment>
            <div>Feature Preview</div>
          </React.Fragment>
        }
      >
        <Link
          external-link="true"
          variant="subtitle2"
          target="__blank"
          href={`${previewHost}/preview?agency=${agency}&siteId=${siteId}&patientheader=true`}
          id="preview-ribbon"
        >
          <VisibilityIcon
            className={classes.previewLink}
            external-link="true"
          />
        </Link>
      </HtmlTooltip>
    ) : null;
  };

  const getUpperRibbonIcons = () =>
    dashboardType === patientDashboard ? (
      <React.Fragment>
        {/* {getSmartChart()} */}
        {getPreviewEnv()}
        <StyledActionIcon
          Icon={RefreshIcon}
          onClick={refreshDashboard}
          id="refresh-ribbon"
        />

        <StyledActionIcon
          Icon={Printer}
          onClick={openPrintInterface}
          id="print-ribbon"
        />
        <MaiaChatAgentRibbon
          HtmlTooltip={HtmlTooltip}
          widgets={[...config, ...(resolveUserNonRoleWidgets ?? [])]}
        />
        <StandingOrdersMenu />
        {readOnlySidebar || <FacesheetMenu />}
        {getDateRestrictionIcon()}
        <PDFContext
          patient={patient}
          currentUser={currentUser}
          agency={agency}
          observation={observation}
          pim={pim}
          keycloak={keycloak}
          clearscripts={clearscripts}
        >
          <PlanOfCare />
        </PDFContext>
      </React.Fragment>
    ) : (
      <>{getPreviewEnv()}</>
    );

  const getLowerRibbonIcons = () => {
    const showLowerRibbon = dashboardType === patientDashboard;
    return showLowerRibbon ? getLowerRibbon() : null;
  };

  const getLowerRibbon = () =>
    readOnlySidebar ? (
      <div className={classes.readOnlyLabel}>
        <span>{defaults.readOnly}</span>
      </div>
    ) : (
      <>
        <FormListMenu />
        <VisitMenu />
        <MinimizedVisit />
      </>
    );

  const patientDisplayName = patient
    ? [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim()
    : '';

  return (
    <>
      <SideBarDiv id="side-bar">
        <SidebarIcon topBarHeight={topBarHeight} id="side-bar-icon">
          <ThemeMenu />
          {getUpperRibbonIcons()}
          <WidgetMenu config={config} isIDGMeeting={!!idgMeeting} />
          <WidgetPlusMenu
            config={resolveUserNonRoleWidgets ?? []}
            onAddPatientDashboardWidget={onAddPatientDashboardWidget}
          />
        </SidebarIcon>
        <LowerSidebarIcon readOnly={readOnlySidebar}>
          <Divider />
          {getLowerRibbonIcons()}
        </LowerSidebarIcon>
        <OrderDialog />
      </SideBarDiv>
      {smartChartEmbeddedOverlayOpen && isEmbeddedDashboard && patient && (
        <ModalDiv
          show
          $coverSidebar
          id="smartchart-embedded-session-overlay"
          role="presentation"
        >
          <Paper
            elevation={8}
            role="dialog"
            aria-labelledby="smartchart-embedded-session-title"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 480,
              margin: 16,
              padding: '24px 32px',
              textAlign: 'center',
            }}
          >
            <Typography
              id="smartchart-embedded-session-title"
              component="p"
              variant="h6"
              style={{ lineHeight: 1.35 }}
            >
              {defaults.smartChartEmbeddedOverlay.replace(
                '{patientName}',
                patientDisplayName || '—',
              )}
            </Typography>
          </Paper>
        </ModalDiv>
      )}
    </>
  );
};
